import React, { useState, useEffect } from "react";
import TopMenuBar from "../../components/TopMenuBar/TopMenuBar";
import NavBar from "../../components/NavBar/NavBar";
import AddonButton from "../../components/AddonButton/AddonButton";
import FinalPackingNavTool from "./FinalPackingNavTool";
import CanvasRenderer from "../../components/CanvasRenderer/CanvasRenderer";
import InfoBox from "./InfoBox";
import { ProgressBar } from "../../components/ProgressBar/ProgressBar";
import PackoutNameForm from "../../components/PackoutNameForm/PackoutNameForm";
import EyeIcon from "../../components/EyeIcon/EyeIcon";
import ShelvesIcon from '@mui/icons-material/Shelves';
import Button from "@mui/material/Button";
import * as Utils from "../../Utils";

import "./../style.css";

const FinalPackingPage = ({ db }) => {
  // --- State variables ---
  const [palletVar, setPalletVar] = useState(() => {
    const saved = localStorage.getItem("palletVar");
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [storeVar, setStoreVar] = useState(() => {
    return localStorage.getItem("storeVar") || "";
  });
  const [quadType, setQuadType] = useState(1);
  const [rows, setRows] = useState([]);
  const [weightVar, setWeightVar] = useState([]);
  const [showAddonButton, setShowAddonButton] = useState(false);
  const [isShelfThere, setIsShelfThere] = useState(true);
  const [loading, setLoading] = useState(true);
  const [settingUpTabs, setSettingUpTabs] = useState(true);
  const [isu90tabCount, setIsu90tabCount] = useState(0);
  const [flat463LtabCount, setFlat463LtabCount] = useState(0);
  const [c463LtabCount, setC463LtabCount] = useState(0);
  const [nfevtabCount, setNfevtabCount] = useState(0);
  const [pallet80x120tabCount, setPallet80x120tabCount] = useState(0);
  const [pallet100x120tabCount, setPallet100x120tabCount] = useState(0);

  const [showAdditionalLoadout, setShowAdditionalLoadout] = useState(false);
  const [selectedBox, setSelectedBox] = useState(undefined);
  const lastLoadOutNum = localStorage.getItem("lastLoadOut");
  const [metricsType, setMetricsType]=useState(0);

  // Bumped after a successful quadrant refresh; CanvasRenderer watches this
  // prop and re-fetches placed_boxes_view when it changes.
  const [canvasRefreshKey, setCanvasRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Triggers the ISU-90 quadrant refresh API and reloads dependent UI state.
  // Called from FinalPackingNavTool (manual button) and InfoBox (auto after
  // a dimension save).
  const handleRefreshQuadrant = async () => {
    if (storeVar !== "ISU-90" || !palletVar || !quadType || refreshing) return;
    // Ask whether the WHOLE pallet should be repacked, or just this quadrant.
    // OK -> full-pallet repack (all quadrants; boxes in other quadrants may
    // move and shelving is regenerated). Cancel -> quadrant-only refresh
    // (other quadrants stay exactly as they are).
    const repackWholePallet = window.confirm(
      `Repack the ENTIRE pallet ${storeVar} ${palletVar}?\n\n` +
        `OK \u2014 repack all quadrants. Boxes in the other quadrants may move ` +
        `and shelving is regenerated.\n` +
        `Cancel \u2014 refresh only the ${quadType} quadrant. The other ` +
        `quadrants stay untouched.`
    );
    setRefreshing(true);
    try {
      const result = await Utils.refreshISU90Quadrant(
        storeVar,
        palletVar,
        quadType,
        undefined, // runId: helper falls back to the current run
        { fullPallet: repackWholePallet }
      );
      if (result && result.ok === false) {
        console.warn("[refresh-quadrant] backend declined:", result);
        alert(
          `Refresh failed (${result.code || "unknown"}):\n${result.message || ""}`
        );
        return;
      }
      if (result && Array.isArray(result.skippedBoxes) && result.skippedBoxes.length > 0) {
        const names = result.skippedBoxes
          .map((b) => `${b.name || b.boxId} (${b.width}x${b.length}x${b.height})`)
          .join("\n  - ");
        alert(
          `Refreshed quadrant: ${result.repacked} repacked, ${result.skipped} skipped.\n` +
          `These boxes no longer fit and were skipped:\n  - ${names}`
        );
      }
      if (result && Number(result.preserved) > 0) {
        // Oversized boxes that span multiple quadrants are not re-packed;
        // they stay where they are. Log for visibility, no user alert.
        console.info(
          `[refresh-quadrant] preserved ${result.preserved} oversized slice(s) in place`
        );
      }
      // Reload weights (per-pallet totals can shift if boxes were skipped)
      // and bump the canvas key so it re-fetches placed_boxes_view.
      await waitForSetWeights();
      setCanvasRefreshKey((k) => k + 1);
      // Clear selectedBox: its position_no may have been renumbered.
      setSelectedBox(undefined);
    } catch (err) {
      console.error("[refresh-quadrant] error:", err);
      alert("Refresh failed. See console for details.");
    } finally {
      setRefreshing(false);
    }
  };

  // --- Fetch weights and counts ---
  async function waitForSetWeights() {
    const tabCol = await Utils.getAllContainersUpdateFromStore();
    if (!tabCol) return;

    let isu90tabCountTemp = 0;
    let flat463LtabCountTemp = 0;
    let c463LtabCountTemp = 0;
    let nfevtabCountTemp = 0;
    let pallet80x120tabCountTemp = 0;
    let pallet100x120tabCountTemp = 0;

    tabCol.forEach((element) => {
      if (element.containertype === "ISU-90") isu90tabCountTemp++;
      if (element.containertype === "463L") flat463LtabCountTemp++;
      if (element.containertype === "C463L") c463LtabCountTemp++;
      if (element.containertype === "NFEV") nfevtabCountTemp++;
      if (element.containertype === "80x120") pallet80x120tabCountTemp++;
      if (element.containertype === "100x120") pallet100x120tabCountTemp++;
    });

    setIsu90tabCount(isu90tabCountTemp);
    setFlat463LtabCount(flat463LtabCountTemp);
    setC463LtabCount(c463LtabCountTemp);
    setNfevtabCount(nfevtabCountTemp);
    setPallet80x120tabCount(pallet80x120tabCountTemp);
    setPallet100x120tabCount(pallet100x120tabCountTemp);

    const newWeights = await Utils.getTotalWeightArray(
      isu90tabCountTemp,
      flat463LtabCountTemp,
      c463LtabCountTemp,
      nfevtabCountTemp,
      pallet80x120tabCountTemp,
      pallet100x120tabCountTemp
    );

    setWeightVar(newWeights);
    console.log("set weight:" + newWeights);
  }

  // --- Fetch rows ---
  async function waitForSetRows() {
    const result = await Utils.getAllItemsPlacedBoxesStore();
    if (!result) return;

    setRows(result);

    if (settingUpTabs) {
      // Auto-select first available pallet type if none selected
      if (result.some((r) => r.storagetype === "ISU-90") && palletVar === 0) {
        setPalletVar(1);
        setStoreVar("ISU-90");
      } else if (
        result.some((r) => r.storagetype === "463L") &&
        palletVar === 0
      ) {
        setPalletVar(1);
        setStoreVar("463L");
      } else if (
        result.some((r) => r.storagetype === "C463L") &&
        palletVar === 0
      ) {
        setPalletVar(1);
        setStoreVar("C463L");
      } else if (
        result.some((r) => r.storagetype === "NFEV") &&
        palletVar === 0
      ) {
        setPalletVar(1);
        setStoreVar("NFEV");
      } else if (
        result.some((r) => r.storagetype === "80x120") &&
        palletVar === 0
      ) {
        setPalletVar(1);
        setStoreVar("80x120");
      } else if (
        result.some((r) => r.storagetype === "100x120") &&
        palletVar === 0
      ) {
        setPalletVar(1);
        setStoreVar("100x120");
      }
      setSettingUpTabs(false);
    }

    /*
    const shelfResult = await Utils.getShelves();
    console.log("get shelvesdata: "+ shelfResult);
    if (shelfResult!=null){
      setIsShelfThere(true);
    }*/

    setLoading(false);
  }

  // --- Check for addon loadout ---
  useEffect(() => {
    async function checkAdditionalLoadout() {
      console.log("lastLoadOutNum: " + lastLoadOutNum);
      if (lastLoadOutNum > 1) {
        const hasExtra = await Utils.checkForLastLoadOut(
          palletVar,
          storeVar,
          quadType,
          lastLoadOutNum
        );
        console.log("lastLoadOutNum + show:" + hasExtra);
        setShowAddonButton(hasExtra);
      } else {
        setShowAddonButton(false);
      }
    }
    checkAdditionalLoadout();
  }, [db, palletVar, storeVar, quadType, lastLoadOutNum]);

  // --- On page load, fetch weights and rows ---
  useEffect(() => {
    waitForSetWeights();
    waitForSetRows();
  }, []);

  // --- Handle pallet selection from nav tool ---
  const handleSelect = ({ quad, palletID, storagetype }) => {
    setQuadType(quad);
    setPalletVar(palletID);
    setStoreVar(storagetype);

    localStorage.setItem("palletVar", palletID);
    localStorage.setItem("storeVar", storagetype);
  };

  const handleShowAddon = () => {
    setShowAdditionalLoadout(true);
  };

  const handleShowShelf =() =>{

  }

  const navigateToExpand = () => {
    const palletData = `${storeVar}:${palletVar}:${quadType}`;
    const data = encodeURIComponent(palletData);
    window.location.href = `#/expandedCanvasPage?data=${data}`;
  };

  //for when boxes are selected
  useEffect(() => {
    // Register callback **before boxes are created**
    Utils.setBoxClickCallback((metadata) => {
      console.log("Updating selectedBox", metadata);
      setSelectedBox(metadata);
    });
  }, []);

  // --- Render ---
  return (
    <div className="finalpacking">
      <TopMenuBar />

      <div className="under-banner">
        <ProgressBar page="packing" />

        <div className="div">
          <div className="overlap-group-wrapper">
            <div className="frame-wrapper"></div>

            <div className="frame-2">
              {/* Packout Name Form */}
              <div className="name-form">
                <div className="left-side">
                  <PackoutNameForm />
                  <p className="directions">
                    <span className="p-bold">
                      5. Check your 3D packing plan.
                    </span>{" "}
                    Navigate through your packing configuration.
                  </p>
                </div>
                <div className="right-side">
                  {!isShelfThere && (
                    <ShelvesIcon
                      className="shelf-button-border"
                      onClick={handleShowShelf}
                    />
                  )}
                  <AddonButton
                    visible={showAddonButton}
                    onClick={handleShowAddon}
                  />

                  <EyeIcon onClick={navigateToExpand} expanded={true} />
                </div>
              </div>

              {/* FinalPackingNavTool */}
              <div className="in-line">
                {!loading && (
                  <FinalPackingNavTool
                    selectedPallet={palletVar}
                    quadType={quadType}
                    storagetype={storeVar}
                    onSelect={handleSelect}
                    weightVar={weightVar}
                    isu90tabCount={isu90tabCount}
                    flat463LtabCount={flat463LtabCount}
                    c463LtabCount={c463LtabCount}
                    nfevtabCount={nfevtabCount}
                    pallet80x120tabCount={pallet80x120tabCount}
                    pallet100x120tabCount={pallet100x120tabCount}
                    onRefreshQuadrant={handleRefreshQuadrant}
                    refreshing={refreshing}
                  />
                )}

                {/* Canvas Renderer */}
                <CanvasRenderer
                  database={db}
                  showButtons={true}
                  print={true}
                  palletID={palletVar}
                  quad={quadType}
                  storagetype={storeVar}
                  style={{ width: "100%", height: "100%" }}
                  layer={false}
                  showAdditionalLoadout={showAdditionalLoadout}
                  setShowAdditionalLoadout={setShowAdditionalLoadout}
                  refreshKey={canvasRefreshKey}
                />
                {/* Info Box to display selected box info */}
                <InfoBox
                  selectedBox={selectedBox}
                  metrics={metricsType}
                  onAfterSave={
                    storeVar === "ISU-90" ? handleRefreshQuadrant : undefined
                  }
                />
              </div>
              <NavBar>
                <button className="button-wrapper">
                  <Button href="#/containerOverviewPage" className="button-9">
                    Back
                  </Button>
                </button>
                <button className="button-7">
                  <Button href="#/donePage" className="button-8">
                    ExportCSV
                  </Button>
                </button>
              </NavBar>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalPackingPage;
